/*
 * Returns a css calc for grid column widths
 *
 * Note: no outer gutters accounted for here... yet
 */
export const gridColumns = (num: number) => {
  return `calc((${space.gridColumn} * ${num}) + (${space.gridGutter} * ${
    num - 1
  }))`;
};

export const breakpoints = ['400px', '1199px', '1200px'];

const ellipsis = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const colors = {
  white: '#fff',
  almostWhite: '#F9FAFA',
  mist: '#f1f1f1',
  mediumGray: '#b7c1cb',
  darkGray: '#525D6A',
  ashGray: '#303D48',
  black: '#202529',
  softTeal: '#C9E7EB',
  lightTeal: '#45A7B6',
  teal: '#21A4B9',
  deepTeal: '#0A5A67',
  softOrange: '#FFF6E9',
  orange: '#ffbb54',
  softLeafyGreen: '#E5F7D9',
  leafyGreen: '#92D779',
  darkLeafyGreen: '#2D9B05',
  softRed: '#FCE7E8',
  red: '#FC6C75',
  darkRed: '#EC4141',
  imperialPurple: '#75377E',
  // -----------
  tableHeader: 'rgba(241, 241, 241, 0.86)',
  modalSidebarBlue: '#f5fafc',
};

export const fonts = {
  primary: 'DM Sans',
  secondary: 'Assistant',
};

export const fontSizes = {
  xsmall: '12px',
  small: '14px',
  medium: '18px',
  body: '21px',
  large: '22px',
  xlarge: '30px',
};

export const fontWeights = {
  normal: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

export const letterSpacings = {
  smallest: '0.01em',
  small: '0.025em',
};

export const lineHeights = {
  xsmall: '.8em',
  small: '1em',
  medium: '1.4em',
  large: '1.6em',
  xlarge: '1.8em',
};

export const text = {
  heading: {
    fontSize: fontSizes.xlarge,
    lineHeight: lineHeights.medium,
    fontFamily: fonts.secondary,
    fontWeight: fontWeights.bold,
  },
  secondaryHeading: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.large,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.medium,
  },
  tertiaryHeading: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.medium,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.medium,
  },
  bodyText: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.small,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.medium,
  },
  small: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.xsmall,
    fontWeight: fontWeights.normal,
    letterSpacing: letterSpacings.smallest,
    lineHeight: lineHeights.small,
  },
  button: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.small,
    fontWeight: fontWeights.normal,
    letterSpacing: letterSpacings.smallest,
    lineHeight: lineHeights.medium,
  },
  forms: {
    input: {
      lineHeight: lineHeights.medium,
      fontSize: fontSizes.small,
    },
    inputLabel: {
      fontSize: fontSizes.small,
      lineHeight: lineHeights.medium,
      fontWeight: fontWeights.medium,
    },
    inputErrorMessage: {
      fontSize: fontSizes.small,
      fontWeight: fontWeights.normal,
      lineHeight: lineHeights.medium,
      color: colors.red,
      fontStyle: 'italic',
    },
    sectionHeading: {
      fontFamily: fonts.secondary,
      fontSize: fontSizes.large,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: colors.mediumGray,
      fontWeight: fontWeights.semiBold,
    },
    sectionDisabledHeading: {
      fontFamily: fonts.secondary,
      fontSize: fontSizes.large,
      fontWeight: fontWeights.normal,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: colors.mediumGray,
    },
    sectionFocusedHeading: {
      fontFamily: fonts.secondary,
      fontSize: fontSizes.large,
      fontWeight: fontWeights.bold,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderBottomColor: colors.imperialPurple,
    },
  },
};

export const radii = {
  small: '6px',
  medium: '8px',
  large: '20px',
};
const spacingUnit = 16;
export const space = {
  quarter: `${spacingUnit / 4}px`,
  half: `${spacingUnit / 2}px`,
  one: `${spacingUnit}px`,
  two: `${2 * spacingUnit}px`,
  three: `${3 * spacingUnit}px`,
  six: `${6 * spacingUnit}px`,
  eight: `${8 * spacingUnit}px`,
  ten: `${10 * spacingUnit}px`,
  gridColumn: `${4 * spacingUnit}px`,
  gridGutter: `${2 * spacingUnit}px`,
};

export const shadows = {
  main: `2px 4px 30px rgba(61, 61, 61, 0.09)`,
  button: `0px 2px 4px ${colors.mediumGray}`,
  topBar: `4px 4px 15px rgba(61, 61, 61, 0.03)`,
  sideBar: `4px 0 6px rgba(61, 61, 61, 0.09)`,
};

const numeric = (numberWithUnits: string): number =>
  parseInt(numberWithUnits.replace(/[a-z]/g, ''), 10);

export const spaceUnitless = Object.fromEntries(
  Object.entries(space).map(([k, v]) => [k, numeric(v)])
);

export const underline = {
  borderBottom: `1.5px solid ${colors.imperialPurple}`,
};

export const textStyles = {
  link: {
    fontFamily: fonts.primary,
    fontWeight: fontWeights.normal,
    fontSize: fontSizes.small,
    lineHeight: lineHeights.medium,
    whiteSpace: 'nowrap',
    color: colors.ashGray,
    textDecoration: 'none',
    ...underline,
    '& svg, & svg path': {
      fill: colors.darkGray,
    },
    ':hover': {
      fontWeight: fontWeights.medium,
      color: colors.deepTeal,
      letterSpacing: letterSpacings.smallest,
      '& svg, & svg path': {
        fill: colors.deepTeal,
      },
    },
    ':active': {
      fontWeight: fontWeights.bold,
      color: colors.deepTeal,
      letterSpacing: letterSpacings.smallest,
      '& svg, & svg path': {
        fill: colors.deepTeal,
      },
    },
  },
};

export const transitions = {
  sideBar: `all 0.2s ease 0s`,
  formSection: `all 0.2s ease 0s`,
  quick: 'all .1s ease 0s',
};

const heights = {
  formField: '40px',
};

const radioGroup = {
  border: 'none',
  marginInlineStart: 'unset',
  marginInlineEnd: 'unset',
  paddingInlineStart: 'unset',
  paddingInlineEnd: 'unset',
  paddingBlockStart: 'unset',
  paddingBlockEnd: 'unset',
  minInlineSize: 'unset',
  legend: {
    paddingInlineStart: 'unset',
    paddingInlineEnd: 'unset',
    border: 'none',
  },
};

const radioPill = {
  width: 'auto',
  height: '32px',
  justifyContent: 'center',
  label: {
    marginRight: space.half,
    ...text.bodyText,
    cursor: 'pointer',
    minWidth: 'fit-content',
    lineHeight: '1.15em',
    whiteSpace: ['wrap', 'wrap', 'nowrap'],
    paddingLeft: space.one,
    paddingRight: space.one,
    paddingTop: '7px',
    paddingBottom: '7px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.darkGray,
    color: colors.darkGray,
    borderRadius: radii.small,
    userSelect: 'none',
    width: 'auto',
    '& svg, & svg path': {
      height: '14px',
      width: '14px',
      marginTop: '1px',
      marginRight: '2px',
      fill: colors.darkGray,
    },
    '&:focus-within': {
      borderColor: colors.teal,
      borderWidth: 1,
      '& svg, & svg path': {
        height: '14px',
        width: '14px',
        marginTop: '1px',
        marginRight: '2px',
      },
    },
    '& > input': {
      minWidth: 0,
      position: 'absolute',
      opacity: 0,
      zIndex: -1,
      width: '1px',
      height: '1px',
      overflow: 'hidden',
    },
  },
};

export const forms = {
  input: {
    ...text.forms.input,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: colors.mediumGray,
    borderRadius: radii.small,
    height: heights.formField,
    padding: space.one,
    error: {
      ...text.forms.input,
      borderColor: colors.red,
      borderRadius: radii.small,
      padding: space.one,
      height: heights.formField,
    },
    resize: 'none',
  },
  label: {
    ...text.forms.inputLabel,
    flexBasis: '100%',
    marginBottom: space.quarter,
    errorMessage: {
      ...text.forms.inputErrorMessage,
      marginBottom: space.half,
    },
  },
  bigLabel: {
    ...text.secondaryHeading,
    flexBasis: '100%',
    marginBottom: space.one,
  },
  section: {
    borderWidth: '1px',
    borderColor: 'transparent',
    borderRadius: radii.medium,
    transition: transitions.formSection,
    heading: {
      ...text.forms.sectionHeading,
      transition: transitions.formSection,
      paddingBottom: space.half,
      marginBottom: space.one,
    },
    disabled: {
      opacity: '.3',
      borderColor: 'transparent',
      borderRadius: radii.medium,
      heading: {
        ...text.forms.sectionDisabledHeading,
        transition: transitions.formSection,
        paddingBottom: space.half,
        marginBottom: space.one,
      },
    },
    focused: {
      transition: transitions.formSection,
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: colors.mediumGray,
      borderRadius: radii.medium,
      boxShadow: shadows.main,
      heading: {
        ...text.forms.sectionFocusedHeading,
        transition: transitions.formSection,
        paddingBottom: space.half,
        marginBottom: space.one,
      },
    },
  },
  classicRadioGroup: {
    ...radioGroup,
    '& > div': {
      flexDirection: 'column',
      '& > div:not(:last-child)': {
        marginBottom: space.one,
      },
    },
  },
  classicRadio: {
    label: {
      ...text.bodyText,
      cursor: 'pointer',
      alignItems: 'center',
      userSelect: 'none',
    },
    unselectedLabel: {
      ...text.bodyText,
      cursor: 'pointer',
      alignItems: 'center',
      userSelect: 'none',
    },
    selectedLabel: {
      ...text.bodyText,
      cursor: 'pointer',
      fontWeight: fontWeights.medium,
      alignItems: 'center',
      userSelect: 'none',
    },
    svg: {
      marginRight: space.half,
      fill: colors.ashGray,
    },
  },
  radioPillGroup: {
    ...radioGroup,
    '& > div': {
      flexWrap: 'wrap',
      marginBottom: `-${space.one}`, // remove row wrapping space from bottom pill row
      '& > div': {
        marginBottom: space.one,
      },
    },
  },
  radioPill: {
    ...radioPill,
    label: {
      ...radioPill.label,
      backgroundColor: colors.mist,
      borderColor: colors.mist,
    },
    selectedLabel: {
      ...radioPill.label,
      backgroundColor: colors.softTeal,
      borderColor: colors.softTeal,
      color: colors.black,
      '& svg, & svg path': {
        height: '14px',
        width: '14px',
        marginTop: '1px',
        marginRight: '2px',
        fill: colors.black,
      },
      '&:focus-within': {
        backgroundColor: colors.softTeal,
        borderColor: colors.softTeal,
        color: colors.darkGray,
        '& svg, & svg path': {
          fill: colors.darkGray,
        },
      },
    },
    unselectedLabel: {
      ...radioPill.label,
      backgroundColor: colors.mist,
      borderColor: colors.mist,
    },
  },
  checkbox: {
    svg: {
      marginRight: space.half,
      fill: colors.ashGray,
      width: 20,
      height: 20,
    },
    uncheckedLabel: {
      ...text.bodyText,
      cursor: 'pointer',
      alignItems: 'center',
      userSelect: 'none',
    },
    checkedLabel: {
      ...text.bodyText,
      cursor: 'pointer',
      alignItems: 'center',
      fontWeight: fontWeights.medium,
      userSelect: 'none',
    },
  },
  dropdown: {
    ...text.forms.input,
    height: heights.formField,
    width: '100%',
    borderRadius: radii.small,
    borderWidth: '1px',
    borderStyle: 'solid',
    appearance: 'none',
    borderColor: colors.mediumGray,
    backgroundRepeat: `no-repeat`,
    backgroundPositionX: `calc(100% - ${space.half})`,
    backgroundPositionY: 'center',
    error: {
      ...text.forms.input,
      height: heights.formField,
      width: '100%',
      borderRadius: radii.small,
      borderWidth: '1px',
      borderColor: colors.red,
      borderStyle: 'solid',
      appearance: 'none',
      padding: space.one,
      backgroundRepeat: `no-repeat`,
      backgroundPositionX: `calc(100% - ${space.half})`,
      backgroundPositionY: 'center',
    },
  },
};

const button = {
  ...text.button,
  height: space.three,
  minWidth: [
    `calc(50% - ${space.one})`,
    `calc(50% - ${space.one})`,
    gridColumns(2),
  ],
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: colors.darkGray,
  textAlign: 'center',
  px: space.one,
  borderRadius: radii.small,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const buttons = {
  primary: {
    ...button,
    backgroundColor: colors.deepTeal,
    borderColor: colors.deepTeal,
    color: colors.white,
    '&:hover, &:active': {
      backgroundColor: colors.deepTeal,
      borderColor: colors.deepTeal,
      boxShadow: shadows.button,
      color: colors.white,
    },
    '&:disabled': {
      borderColor: colors.mist,
      backgroundColor: colors.mist,
      color: colors.ashGray,
      cursor: 'default',
      '&:hover, &:active': {
        boxShadow: 'none',
      },
      '&>svg, &>svg path': {
        fill: colors.ashGray,
        mr: space.half,
      },
    },
    '&>svg, &>svg path': {
      fill: colors.white,
      mr: space.half,
    },
  },
  secondary: {
    ...button,
    borderWidth: 2,
    backgroundColor: colors.white,
    borderColor: colors.deepTeal,
    color: colors.deepTeal,
    '&>svg, &>svg path': {
      fill: colors.deepTeal,
      mr: space.half,
    },
    '&:hover, &:active': {
      borderWidth: 2,
      borderColor: colors.deepTeal,
      color: colors.deepTeal,
      boxShadow: `0 4px 4px ${colors.mist}`,
      // boxShadow: shadows.button,
      '&>svg, &>svg path': {
        fill: colors.deepTeal,
        mr: space.half,
      },
    },
    '&:disabled': {
      borderColor: colors.mediumGray,
      backgroundColor: colors.white,
      color: colors.mediumGray,
      cursor: 'default',
      '&>svg, &>svg path': {
        fill: colors.mediumGray,
        mr: space.half,
      },
      '&:hover, &:active': {
        boxShadow: 'none',
      },
    },
  },
  text: {
    ...button,
    backgroundColor: colors.white,
    color: colors.ashGray,
    borderColor: 'transparent',
    '&>svg, &>svg path': {
      fill: colors.ashGray,
      mr: space.half,
    },
    '&:hover, &: active': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
      color: colors.teal,
      '&>svg, &>svg path': {
        fill: colors.teal,
        mr: space.half,
      },
    },
    '&:disabled': {
      color: colors.mediumGray,
      borderColor: colors.mediumGray,
      textDecoration: 'none',
      cursor: 'default',
      '&>svg, &>svg path': {
        fill: colors.mediumGray,
        mr: space.half,
      },
    },
  },
};

export const cards = {
  info: {
    backgroundColor: colors.softLeafyGreen,
    borderRadius: radii.small,
    padding: space.one,
  },
  secondaryBox: {
    borderStyle: 'solid',
    borderWidth: '0.5px',
    borderColor: colors.mediumGray,
    p: space.one,
  },
};

const tab = {
  mr: space.one,
  height: '.2em',
  borderRadius: 'calc(0.2em / 2)', // half height
  boxShadow: '0 1.6em 0 0 transparent',
  fontSize: '1em',
  background: 'transparent',
  px: '.15em',
};

export const tabs = {
  tabList: {
    borderBottom: `1px solid ${colors.mediumGray}`,
    height: '1.7666em',
    fontSize: fontSizes.medium,
    mb: space.two,
  },
  tab: {
    ...tab,
    selected: {
      ...tab,
      color: '#000',
      boxShadow: '0 1.6em 0 0 #000',
    },
  },
};

const tableCell = {
  fontFamily: fonts.primary,
  fontSize: fontSizes.small,
  fontWeight: fontWeights.normal,
  letterSpacing: letterSpacings.smallest,
  lineHeight: lineHeights.small,
  color: colors.ashGray,
  py: space.one,
  pr: space.two,
  pl: 0,
  borderColor: colors.mediumGray,
  borderBottomWidth: 1,
  borderBottomStyle: 'solid',
  ...ellipsis,
};

export const variants = {
  dividers: {
    horizontal: {
      width: '100%',
      height: 0,
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: colors.mediumGray,
    },
    vertical: {
      width: 1,
      backgroundColor: colors.mediumGray,
      height: '100%',
      border: 'none',
    },
  },
  table: {
    fontFamily: fonts.secondary,
    fontSize: fontSizes.medium,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.large,
    borderRadius: radii.medium,
    borderSpacing: 0,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.mediumGray,
    width: '100%',
    color: colors.ashGray,
    tableLayout: 'fixed',
  },
  tableCell: {
    ...tableCell,
    '&:first-of-type': {
      paddingLeft: ['auto', 'auto', space.two],
    },
    'tr:last-child &': {
      borderBottom: '0 none',
    },
  },
  tableCellCentered: {
    ...tableCell,
    px: space.one,
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    textAlign: 'center',
    'tr:last-child &': {
      borderBottom: '0 none',
    },
  },
  tableHeader: {
    fontFamily: fonts.primary,
    fontSize: fontSizes.xsmall,
    fontWeight: fontWeights.bold,
    letterSpacing: letterSpacings.smallest,
    lineHeight: lineHeights.small,
    textAlign: 'left',
    borderColor: colors.mediumGray,
    textTransform: 'uppercase',
    py: '2em',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    '&:first-of-type': {
      paddingLeft: space.two,
    },
    ...ellipsis,
  },
};

const toast = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: radii.medium,
  fontSize: fontSizes.small,
  lineHeight: '1.5em',
  padding: space.one,
};

export const toasts = {
  default: {
    ...toast,
    borderColor: colors.modalSidebarBlue,
    backgroundColor: colors.modalSidebarBlue,
  },
  warning: {
    ...toast,
    borderColor: colors.orange,
    backgroundColor: colors.orange,
  },
  error: {
    ...toast,
    borderColor: colors.darkRed,
    backgroundColor: colors.red,
  },
};

export const globalStyles = {
  '*:not(.BeaconContainer *)': {
    boxSizing: 'border-box',
    color: colors.ashGray,
  },
  a: { textDecoration: 'none' },
  ul: { margin: 0, padding: 0, listStyle: 'none' },
  '.DayPickerInput': {
    '&, & > input': {
      width: '100%',
      boxSizing: 'border-box',
    },
    '& > input': {
      ...forms.input,
    },
  },
  '.DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside), .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside):hover': {
    backgroundColor: `${colors.softTeal} !important`,
    color: `${colors.ashGray} !important`,
    fontWeight: `${fontWeights.medium} !important`,
  },
};
